import type { ReviewComment } from '@/stores/reviewStore';

/** AI 深度审查：修改建议回复前缀（与正文拆分） */
export const AI_REVIEW_SUGGESTION_REPLY_PREFIX = '（署名一诺） 建议修改为：';

const INLINE_SUGGESTION_RE = /(?:（署名一诺）\s*)?建议修改为：\s*/;

/** 深度审查三层分类码（与 ai-service 一致） */
export const DEEP_REVIEW_CATEGORY_LOGIC = 'logic_self';
export const DEEP_REVIEW_CATEGORY_COMPLIANCE = 'compliance';
export const DEEP_REVIEW_CATEGORY_CONSISTENCY = 'consistency';

/** 评论卡片正文展示用错误类型标签 */
export const DEEP_REVIEW_ERROR_LABELS: Record<string, string> = {
  [DEEP_REVIEW_CATEGORY_LOGIC]: '逻辑错误',
  [DEEP_REVIEW_CATEGORY_COMPLIANCE]: '规范性错误',
  [DEEP_REVIEW_CATEGORY_CONSISTENCY]: '关联性错误',
  logic: '逻辑错误',
  standards: '规范性错误',
  relevance: '关联性错误',
};

export function deepReviewErrorLabel(category: string): string {
  const key = (category || '').trim();
  if (!key) return '审查';
  return DEEP_REVIEW_ERROR_LABELS[key] || key;
}

export function isAiReviewComment(node: { user_id?: string; author?: string }) {
  const userId = node.user_id;
  const author = node.author;
  return userId === 'yino-ai' || author === '一诺';
}

export function buildAiReviewProblemContent(category: string | string[], rationale?: string) {
  const codes = (Array.isArray(category) ? category : [category]).map((c) => String(c || '').trim()).filter(Boolean);
  const labels: string[] = [];
  for (const code of codes) {
    const label = deepReviewErrorLabel(code);
    if (label && !labels.includes(label)) labels.push(label);
  }
  const head = labels.map((l) => `【${l}】`).join('');
  const lines = [head, (rationale || '').trim()].filter(Boolean);
  return lines.join('\n');
}

function normalizeAnchorKey(text: string): string {
  return (text || '').replace(/\s+/g, '').trim();
}

function anchorsAreDuplicate(a: string, b: string): boolean {
  const ka = normalizeAnchorKey(a);
  const kb = normalizeAnchorKey(b);
  if (!ka || !kb) return false;
  if (ka === kb) return true;
  const shorter = ka.length <= kb.length ? ka : kb;
  const longer = ka.length <= kb.length ? kb : ka;
  return shorter.length > 0 && longer.includes(shorter) && shorter.length / longer.length >= 0.72;
}

export interface DeepReviewFindingLike {
  anchor_text: string;
  paragraph_label?: string;
  category: string;
  suggested_replacement?: string;
  rationale?: string;
  categories?: string[];
}

/** 与 ai-service 一致：按原文锚点去重，合并多种错误类型 */
export function dedupeDeepReviewFindings<T extends DeepReviewFindingLike>(findings: T[]): T[] {
  const out: T[] = [];
  for (const raw of findings) {
    const anchor = (raw.anchor_text || '').trim();
    if (!anchor) continue;
    const incomingCats = [
      ...(Array.isArray(raw.categories) ? raw.categories : []),
      raw.category,
    ]
      .map((c) => String(c || '').trim())
      .filter(Boolean);

    const existingIdx = out.findIndex((ex) =>
      (!raw.paragraph_label || !ex.paragraph_label || raw.paragraph_label === ex.paragraph_label)
      && anchorsAreDuplicate(anchor, ex.anchor_text || ''));
    if (existingIdx < 0) {
      const cats = [...new Set(incomingCats)];
      out.push({
        ...raw,
        anchor_text: anchor,
        categories: cats,
        category: cats[0] || raw.category,
      });
      continue;
    }
    const ex = out[existingIdx]!;
    if (!ex.paragraph_label && raw.paragraph_label) ex.paragraph_label = raw.paragraph_label;
    const mergedCats = [...new Set([...(ex.categories || [ex.category]), ...incomingCats])];
    ex.categories = mergedCats;
    ex.category = mergedCats[0] || ex.category;
    const rats = [ex.rationale, raw.rationale]
      .map((r) => (r || '').trim())
      .filter(Boolean)
      .filter((r, i, arr) => arr.indexOf(r) === i);
    ex.rationale = rats.join('；');
    const replOld = (ex.suggested_replacement || '').trim();
    const replNew = (raw.suggested_replacement || '').trim();
    if (replNew.length > replOld.length) ex.suggested_replacement = replNew;
  }
  return out;
}

export function buildAiReviewSuggestionReply(suggestedReplacement: string) {
  return `${AI_REVIEW_SUGGESTION_REPLY_PREFIX}${(suggestedReplacement || '').trim()}`;
}

export function extractSuggestionFromReplyContent(content: string): string {
  const raw = (content || '').trim();
  if (!raw) return '';
  if (raw.startsWith(AI_REVIEW_SUGGESTION_REPLY_PREFIX)) {
    return raw.slice(AI_REVIEW_SUGGESTION_REPLY_PREFIX.length).trim();
  }
  const m = raw.match(INLINE_SUGGESTION_RE);
  if (m && m.index != null) {
    return raw.slice(m.index + m[0].length).trim();
  }
  return '';
}

export function splitAiReviewInlineContent(content: string): { problem: string; suggestion: string } {
  const raw = (content || '').trim();
  if (!raw) return { problem: '', suggestion: '' };
  const m = raw.match(INLINE_SUGGESTION_RE);
  if (m && m.index != null) {
    return {
      problem: raw.slice(0, m.index).trim(),
      suggestion: raw.slice(m.index + m[0].length).trim(),
    };
  }
  return { problem: raw, suggestion: '' };
}

export function isAiSuggestionReply(node: ReviewComment) {
  if (!isAiReviewComment(node)) return false;
  return INLINE_SUGGESTION_RE.test(node.content || '');
}

export interface ParsedAiReviewComment {
  problemContent: string;
  suggestion: string;
  /** 来自子回复「（署名一诺） 建议修改为：」 */
  suggestionFromReply: boolean;
}

export function parseAiReviewComment(node: ReviewComment): ParsedAiReviewComment | null {
  if (!isAiReviewComment(node)) return null;
  const children = node.children || [];
  const suggestionReply = children.find((c) => isAiSuggestionReply(c));
  if (suggestionReply) {
    return {
      problemContent: (node.content || '').trim(),
      suggestion: extractSuggestionFromReplyContent(suggestionReply.content),
      suggestionFromReply: true,
    };
  }
  const split = splitAiReviewInlineContent(node.content || '');
  return {
    problemContent: split.problem,
    suggestion: split.suggestion,
    suggestionFromReply: false,
  };
}
